import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {validateEmail, validateFieldLength, validateNINumber} from '../../../utility/validationUtil';
import moment from 'moment';
import {EditableFields} from '../../../shared-components/EditableFields';
import useTextFields from '../../../utility/hooks/useTextFields';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import {Backdrop, Box, Button, CircularProgress, Stack, Tooltip} from '@mui/material';
import AdmicityTableV2 from '../../../shared-components/Table/V2/AdmicityTableV2';
import ProfileInfoSection from '../../../shared-components/ProfileInfoSection';
import {useConfirm} from 'material-ui-confirm';
import AdmicityAddressLookup from '../../../shared-components/AdmicityAddressLookup';
import {
    useGetContactTitlesQuery,
    useGetEmployeeContactTypesQuery,
    useGetNextOfKinTypesQuery
} from '../../../api/services/lookupsService';
import {useUpdateEmployeeProfileMutation} from '../../../api/services/employeeService';
import useNotification from '../../../utility/hooks/useNotification';
import {useDispatch} from 'react-redux';
import AdmicityDialog from '../../../shared-components/AdmicityDialog';
import AdmicityForm from '../../../shared-components/AdmicityForm';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';

const EMPTY = [];

const EmployeeProfile = ({data, isLoading}) => {
    const confirm = useConfirm();
    const dispatch = useDispatch();
    const {data: titles = EMPTY, isLoading: isTitlesLoading} = useGetContactTitlesQuery();
    const {data: nextOfKinTypes = EMPTY, isLoading: isNextOfKinTypesLoading} = useGetNextOfKinTypesQuery();
    const {data: employeeContactTypes = EMPTY, isLoading: isContactTypesLoading} = useGetEmployeeContactTypesQuery();
    const [isEditMode, setIsEditMode] = useState(false);
    const [updateEmployeeProfile, {
        isSuccess: isUpdateEmployeeProfileSuccess,
        isError: isUpdateEmployeeProfileError,
        error
    }] = useUpdateEmployeeProfileMutation();
    const {showErrorNotification, showSuccessNotification, showDetailedErrorNotification} = useNotification();
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [selectedNextOfKinAddress, setSelectedNextOfKinAddress] = useState(null);
    const [cars, setCars] = useState(data?.cars || []);
    const [openCarDialog, setOpenCarDialog] = useState(false);
    const [editingCar, setEditingCar] = useState(null);
    const [contacts, setContacts] = useState(data?.contacts || []);
    const [openContactDialog, setOpenContactDialog] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [nextOfKinAddressKey, setNextOfKinAddressKey] = useState(0);
    const ignoreAddressSelect = useCallback(() => undefined, []);
    const isSomeQueryPending = isLoading || isTitlesLoading || isNextOfKinTypesLoading || isContactTypesLoading;
    const homeAddressDataKey = useMemo(() => JSON.stringify(data?.address || null), [data?.address]);
    const nextOfKinAddressDataKey = useMemo(() => JSON.stringify(data?.nextOfKinAddress || null), [data?.nextOfKinAddress]);

    useEffect(() => {
        if (isUpdateEmployeeProfileSuccess) {
            showSuccessNotification('Profile updated successfully');
            setIsEditMode(false);
        } else if (isUpdateEmployeeProfileError) {
            if (error?.data?.validationErrors) {
                showDetailedErrorNotification('Failed to submit form', error.data.validationErrors);
            } else {
                showErrorNotification('Failed to update profile');
            }
        }
    }, [isUpdateEmployeeProfileSuccess, isUpdateEmployeeProfileError]);

    useEffect(() => {
        if (!isEditMode) {
            setCars(data?.cars || []);
            setContacts(data?.contacts || []);
            setSelectedAddress(data?.address || null);
            setSelectedNextOfKinAddress(data?.nextOfKinAddress || null);
        }
    }, [data?.cars, data?.contacts, data?.address, data?.nextOfKinAddress, isEditMode]);

    const basicInfoTextFields = useMemo(() =>
        [
            {
                id: 'titleId',
                label: 'Title',
                name: 'titleId',
                initialValue: data?.titleId,
                required: false,
                type: 'select',
                select: true,
                nullableOnEmpty: true,
                options: titles?.map(title => ({
                    title: title.description,
                    value: title.id,
                }))
            },
            {
                id: 'profile_forename',
                label: 'Forename',
                name: 'legalForename',
                initialValue: data?.legalForename,
                required: true,
                validate: (value) => validateFieldLength(value, 60),
            },
            {
                id: 'profile_middleName',
                label: 'Middle Name',
                name: 'middleName',
                initialValue: data?.middleName,
                required: false,
                validate: (value) => validateFieldLength(value, 60)
            },
            {
                id: 'profile_surname',
                label: 'Surname',
                name: 'legalSurname',
                initialValue: data?.legalSurname,
                required: true,
                validate: (value) => validateFieldLength(value, 60)
            },
            {
                id: 'profile_preferredForename',
                label: 'Preferred Forename',
                name: 'preferredForename',
                initialValue: data?.preferredForename,
                required: true,
                validate: (value) => validateFieldLength(value, 60)
            },
            {
                id: 'profile_preferredSurname',
                label: 'Preferred Surname',
                name: 'preferredSurname',
                initialValue: data?.preferredSurname,
                required: true,
                validate: (value) => validateFieldLength(value, 60)
            },
            {
                id: 'profile_dateOfBirth',
                label: 'Date Of Birth',
                name: 'dateOfBirth',
                type: 'date',
                inputType: 'date',
                initialValue: data?.dateOfBirth ? moment.utc(data?.dateOfBirth) : null,
                required: true,
                minDate: moment(new Date('1900-01-01')),
                maxDate: moment().subtract(12, 'years'),
                nullableOnEmpty: true
            },
            {
                id: 'profile_email',
                label: 'Email',
                name: 'email',
                initialValue: data?.email,
                required: true,
                validate: validateEmail,
                nullableOnEmpty: true
            },
            {
                id: 'profile_phone',
                label: 'Mobile Number',
                name: 'mobileNumber',
                initialValue: data?.mobileNumber,
                validate: (value) => validateFieldLength(value, 20, false),
                required: false
            }
        ], [data, titles]);

    const nextOfKinTextFields = useMemo(() =>
        [
            {
                id: 'nextOfKinForename',
                label: 'Forename',
                name: 'nextOfKinForename',
                initialValue: data?.nextOfKinForename,
                validate: (value) => validateFieldLength(value, 60, false),
                required: false,
                dependencies: ['nextOfKinSurname']
            },
            {
                id: 'nextOfKinMiddleName',
                label: 'Middle Name',
                name: 'nextOfKinMiddleName',
                initialValue: data?.nextOfKinMiddleName,
                validate: (value) => validateFieldLength(value, 60, false),
                required: false,
                dependencies: ['nextOfKinSurname']
            },
            {
                id: 'nextOfKinSurname',
                label: 'Surname',
                name: 'nextOfKinSurname',
                initialValue: data?.nextOfKinSurname,
                validate: (value, fields) => {
                    const hasOtherFields = Object.entries(fields).some(([key, field]) => {
                        if (key === 'nextOfKinSurname') return false;
                        const value = field.textFieldProps.value;
                        return value !== undefined && value !== null && value !== '';
                    });

                    if (!hasOtherFields) {
                        return {
                            isValid: true,
                            message: ''
                        };
                    }

                    if (!value || value.trim() === '') {
                        return {
                            isValid: false,
                            message: 'Surname is required when other next of kin information is provided'
                        };
                    }

                    const lengthValidation = validateFieldLength(value, 60);
                    return {
                        isValid: lengthValidation.isValid,
                        message: lengthValidation.message
                    };
                },
                required: false
            },
            {
                id: 'nextOfKinTelephone',
                label: 'Telephone',
                name: 'nextOfKinTelephone',
                initialValue: data?.nextOfKinTelephone,
                validate: (value) => validateFieldLength(value, 20, false),
                required: false,
                dependencies: ['nextOfKinSurname']
            },
            {
                id: 'nextOfKinTitleCode',
                label: 'Title',
                name: 'nextOfKinTitleCode',
                initialValue: data?.nextOfKinTitleCode,
                required: false,
                type: 'select',
                select: true,
                nullableOnEmpty: true,
                options: titles?.map(title => ({
                    title: title.description,
                    value: title.code,
                })),
                dependencies: ['nextOfKinSurname']
            },
            {
                id: 'nextOfKinTypeCode',
                label: 'Relationship',
                name: 'nextOfKinTypeCode',
                initialValue: data?.nextOfKinTypeCode,
                required: false,
                type: 'select',
                select: true,
                nullableOnEmpty: true,
                options: nextOfKinTypes?.map(t => ({
                    title: t.description,
                    value: t.code,
                })),
                dependencies: ['nextOfKinSurname']
            }
        ], [data, titles, nextOfKinTypes]);

    const identificationBankingDetailsTextFields = useMemo(() =>
        [
            {
                id: 'profile_NINumber',
                label: 'NI Number',
                name: 'niNumber',
                initialValue: data?.niNumber,
                required: false,
                validate: validateNINumber
            },
            {
                id: 'profile_bankName',
                label: 'Bank Name',
                name: 'bankName',
                initialValue: data?.bankName,
                required: false,
                validate: (value) => validateFieldLength(value, 50),
            },
            {
                id: 'profile_bankSortCode',
                label: 'Bank Sort Code',
                name: 'bankSortCode',
                initialValue: data?.bankSortCode,
                required: false,
                validate: (value) => {
                    if (!value) {
                        return {
                            isValid: true,
                            message: ''
                        };
                    }
                    const cleanValue = value.replace(/\s/g, '');
                    if (!/^\d+$/.test(cleanValue)) {
                        return {
                            isValid: false,
                            message: 'Sort code must contain only digits'
                        };
                    }
                    if (cleanValue.length !== 6) {
                        return {
                            isValid: false,
                            message: 'Sort code must be 6 digits'
                        };
                    }
                    return {
                        isValid: true,
                        message: ''
                    };
                }
            },
            {
                id: 'profile_bankAccount',
                label: 'Bank Account',
                name: 'bankAccount',
                initialValue: data?.bankAccount,
                required: false,
                validate: (value) => {
                    if (!value) {
                        return {
                            isValid: true,
                            message: ''
                        };
                    }
                    if (value.length > 15) {
                        return {
                            isValid: false,
                            message: 'Bank account cannot exceed 15 characters'
                        };
                    }
                    if (!/^[a-zA-Z0-9]+$/.test(value)) {
                        return {
                            isValid: false,
                            message: 'Bank account must contain only letters and numbers'
                        };
                    }
                    return {
                        isValid: true,
                        message: ''
                    };
                }
            }
        ], [data]);

    const {
        fields: basicInfoFields,
        getFieldValues: getBasicInfoFieldValues
    } = useTextFields(basicInfoTextFields);

    const {
        fields: identificationBankingFields,
        getFieldValues: getIdentificationBankingFieldValues
    } = useTextFields(identificationBankingDetailsTextFields);

    const {
        fields: nextOfKinFields,
        getFieldValues: getNextOfKinFieldsFieldValues
    } = useTextFields(nextOfKinTextFields);

    const carFormFields = [
        {
            label: 'Registration',
            name: 'registration',
            required: true,
            validate: (value) => {
                if (!value || value.trim() === '') {
                    return {
                        isValid: false,
                        message: 'Registration is required'
                    };
                }
                if (value.length > 12) {
                    return {
                        isValid: false,
                        message: 'Registration cannot exceed 12 characters'
                    };
                }
                return {
                    isValid: true,
                    message: ''
                };
            }
        },
        {
            label: 'Color',
            name: 'color',
            required: false,
            validate: (value) => validateFieldLength(value, 32)
        },
        {
            label: 'Model',
            name: 'model',
            required: false,
            validate: (value) => validateFieldLength(value, 32)
        }
    ];

    const contactFormFields = useMemo(() => [
        {
            label: 'Forename',
            name: 'forename',
            required: true,
            validate: (value) => {
                if (!value || value.trim() === '') {
                    return {
                        isValid: false,
                        message: 'Forename is required'
                    };
                }
                if (value.length > 60) {
                    return {
                        isValid: false,
                        message: 'Forename cannot exceed 60 characters'
                    };
                }
                return {
                    isValid: true,
                    message: ''
                };
            }
        },
        {
            label: 'Surname',
            name: 'surname',
            required: true,
            validate: (value) => {
                if (!value || value.trim() === '') {
                    return {
                        isValid: false,
                        message: 'Surname is required'
                    };
                }
                if (value.length > 60) {
                    return {
                        isValid: false,
                        message: 'Surname cannot exceed 60 characters'
                    };
                }
                return {
                    isValid: true,
                    message: ''
                };
            }
        },
        {
            label: 'Type',
            name: 'typeCode',
            required: false,
            inputType: 'select',
            select: true,
            nullableOnEmpty: true,
            options: employeeContactTypes?.map(t => ({
                title: t.description,
                value: t.code,
            }))
        },
        {
            label: 'Email',
            name: 'email',
            required: false,
            validate: validateEmail
        },
        {
            label: 'Phone',
            name: 'phone',
            required: false,
            validate: (value) => validateFieldLength(value, 20)
        }
    ], [employeeContactTypes]);

    const handleAddCar = () => {
        setEditingCar(null);
        setOpenCarDialog(true);
    };

    const handleEditCar = (car) => {
        setEditingCar(car);
        setOpenCarDialog(true);
    };

    const handleDeleteCar = (car) => {
        setCars(prev => prev.filter(c => c !== car));
    };

    const handleSaveCar = (carData) => {
        if (editingCar) {
            setCars(prev => prev.map(c => (c === editingCar ? carData : c)));
        } else {
            setCars(prev => [...prev, carData]);
        }
        setOpenCarDialog(false);
    };

    const getCarActions = (car) => [
        {
            icon: <EditIcon/>,
            onClick: () => handleEditCar(car)
        },
        {
            icon: <DeleteIcon/>,
            onClick: () => handleDeleteCar(car)
        }
    ];

    const handleAddContact = () => {
        setEditingContact(null);
        setOpenContactDialog(true);
    };

    const handleEditContact = (contact) => {
        setEditingContact(contact);
        setOpenContactDialog(true);
    };

    const handleDeleteContact = (contact) => {
        setContacts(prev => prev.filter(c => c !== contact));
    };

    const handleSaveContact = (contactData) => {
        if (editingContact) {
            setContacts(prev => prev.map(c => (c === editingContact ? contactData : c)));
        } else {
            setContacts(prev => [...prev, contactData]);
        }
        setOpenContactDialog(false);
    };

    const getContactActions = (contact) => [
        {
            icon: <EditIcon/>,
            onClick: () => handleEditContact(contact)
        },
        {
            icon: <DeleteIcon/>,
            onClick: () => handleDeleteContact(contact)
        }
    ];

    const toSaveAddress = (addr) => {
        if (!addr) return null;
        return {
            ...addr,
            country: addr?.countryCode
        };
    };

    const handleSave = () => {
        confirm({
            title: 'Confirmation',
            description: 'Are you sure that you want to update the profile?',
            confirmationText: 'Confirm'
        })
            .then(() => {
                const body = {
                    id: data.id,
                    ...getBasicInfoFieldValues(),
                    ...getIdentificationBankingFieldValues(),
                    ...getNextOfKinFieldsFieldValues(),
                    address: toSaveAddress(selectedAddress),
                    nextOfKinAddress: toSaveAddress(selectedNextOfKinAddress),
                    cars,
                    contacts
                };
                dispatch(updateEmployeeProfile(body));
            })
            .catch(() => {
            });
    };

    const isAddressValid = (address) => {
        if (!address) return true;
        const fields = ['apartment', 'houseName', 'houseNumber', 'street', 'district', 'town', 'county', 'postcode'];
        const filled = fields.filter(f => address?.[f]);
        if (filled.length === 0) return true;
        return Boolean(address?.postcode || address?.town || address?.street);
    };

    const isNextOfKinValid = () => {
        const hasNextOfKinFields = Object.values(nextOfKinFields).some(field => {
            const value = field.textFieldProps.value;
            return value !== undefined && value !== null && value !== '';
        });

        const hasNextOfKinAddress = selectedNextOfKinAddress && Object.values(selectedNextOfKinAddress).some(value =>
            value !== undefined && value !== null && value !== ''
        );

        if (!hasNextOfKinFields && !hasNextOfKinAddress) return true;

        return nextOfKinFields.nextOfKinSurname?.textFieldProps.value !== undefined &&
            nextOfKinFields.nextOfKinSurname?.textFieldProps.value !== null &&
            nextOfKinFields.nextOfKinSurname?.textFieldProps.value !== '';
    };

    /* eslint-disable react/prop-types */
    const areTextFieldsValid = (fieldsObj) => {
        if (!fieldsObj || typeof fieldsObj !== 'object') return false;
        return Object.values(fieldsObj).every(f => {
            const props = f.textFieldProps;
            if (props.required) {
                return props.value !== undefined && props.value !== null && props.value !== '' && !props.error;
            }
            return !props.error;
        });
    };
    /* eslint-enable react/prop-types */

    const isFormValid =
        areTextFieldsValid(basicInfoFields) &&
        areTextFieldsValid(identificationBankingFields) &&
        isAddressValid(selectedAddress) &&
        isAddressValid(selectedNextOfKinAddress) &&
        isNextOfKinValid();

    const handleClearNextOfKin = () => {
        confirm({
            title: 'Clear Next of Kin',
            description: 'Are you sure you want to clear all next of kin information?',
            confirmationText: 'Clear'
        })
            .then(() => {
                Object.keys(nextOfKinFields).forEach(fieldName => {
                    const field = nextOfKinFields[fieldName];
                    if (field?.textFieldProps?.onChange) {
                        field.textFieldProps.onChange({target: {name: fieldName, value: ''}});
                    }
                });

                setSelectedNextOfKinAddress(null);
                setNextOfKinAddressKey(prev => prev + 1);
            })
            .catch(() => {
            });
    };

    return (
        <Box flexGrow={1} position="relative">
            {
                isSomeQueryPending
                    ? null
                    : <>
                        {data && (
                            <Grid container spacing={{
                                xs: 2,
                                md: 2
                            }}>
                                <Grid item xs={12} md={6}>
                                    <ProfileInfoSection title="Basic Information">
                                        <EditableFields fields={basicInfoFields} edit={isEditMode}/>
                                    </ProfileInfoSection>

                                    <ProfileInfoSection title="Identification & Banking Details">
                                        <EditableFields fields={identificationBankingFields} edit={isEditMode}/>
                                    </ProfileInfoSection>

                                    <ProfileInfoSection title="Home Address">
                                        <AdmicityAddressLookup
                                            key={isEditMode ? 'home-edit' : `home-view-${homeAddressDataKey}`}
                                            onAddressSelect={isEditMode ? setSelectedAddress : ignoreAddressSelect}
                                            readonly={!isEditMode}
                                            initialValue={isEditMode ? selectedAddress : data?.address}
                                            defaultCountry={''}
                                        />
                                    </ProfileInfoSection>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <ProfileInfoSection title={
                                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                            <span>Cars</span>
                                            {isEditMode && (
                                                <Button
                                                    color="primary"
                                                    variant="contained"
                                                    startIcon={<AddCircleOutlineIcon/>}
                                                    onClick={handleAddCar}
                                                    size="medium"
                                                >
                                                    Add Car
                                                </Button>
                                            )}
                                        </Box>
                                    }>
                                        {cars && cars.length > 0 && (
                                            <AdmicityTableV2
                                                data={cars}
                                                columns={[
                                                    {
                                                        accessorKey: 'registration',
                                                        header: 'Registration'
                                                    },
                                                    {
                                                        accessorKey: 'color',
                                                        header: 'Color'
                                                    },
                                                    {
                                                        accessorKey: 'model',
                                                        header: 'Model'
                                                    }
                                                ]}
                                                tableProps={{
                                                    enableSorting: false,
                                                    enableColumnResizing: false,
                                                    enableBottomToolbar: false,
                                                    enableTopToolbar: false,
                                                    enableRowActions: isEditMode,
                                                    muiTableContainerProps: {
                                                        sx: {
                                                            maxHeight: 500,
                                                            minHeight: 'auto'
                                                        }
                                                    },
                                                    muiTableBodyRowProps: {hover: false},
                                                    renderRowActions: isEditMode
                                                        ? ({row}) => (
                                                            <Box display="flex" flexWrap="nowrap" gap={1}>
                                                                {getCarActions(row.original).map((action, idx) => (
                                                                    <Tooltip
                                                                        key={idx}
                                                                        title={!row.original.simsId && row.original.id
                                                                            ? 'The car cannot be edited before it will be synchronized with SIMS'
                                                                            : null}
                                                                    >
                                                                        <span>
                                                                            <IconButton
                                                                                key={idx}
                                                                                onClick={action.onClick}
                                                                                disabled={!row.original.simsId && row.original.id}
                                                                            >
                                                                                {action.icon}
                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                ))}
                                                            </Box>
                                                        )
                                                        : undefined
                                                }}
                                            />
                                        )}

                                        <AdmicityDialog
                                            handleClose={() => setOpenCarDialog(false)}
                                            title={editingCar ? 'Edit Car' : 'Add Car'}
                                            open={openCarDialog}
                                            actions={[]}
                                        >
                                            <AdmicityForm
                                                id="car-form"
                                                textFields={carFormFields.map(field => ({
                                                    ...field,
                                                    initialValue: editingCar ? editingCar[field.name] : ''
                                                }))}
                                                handleSubmit={handleSaveCar}
                                                buttonText="Save"
                                                actionsContainerProps={{container: {display: 'flex'}}}
                                            />
                                        </AdmicityDialog>
                                    </ProfileInfoSection>

                                    <ProfileInfoSection title={
                                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                            <span>Contacts</span>
                                            {isEditMode && (
                                                <Button
                                                    color="primary"
                                                    variant="contained"
                                                    startIcon={<AddCircleOutlineIcon/>}
                                                    onClick={handleAddContact}
                                                    size="medium"
                                                >
                                                    Add Contact
                                                </Button>
                                            )}
                                        </Box>
                                    }>
                                        {contacts && contacts.length > 0 && (
                                            <AdmicityTableV2
                                                data={contacts}
                                                columns={[
                                                    {
                                                        id: 'fullName',
                                                        sortable: false,
                                                        minWidth: 200,
                                                        accessorKey: 'fullName',
                                                        header: 'Full name',
                                                        Cell: ({row}) => `${row?.original.forename || ''} ${row?.original.surname || ''}`
                                                    },
                                                    {
                                                        id: 'typeCode',
                                                        accessorKey: 'typeCode',
                                                        header: 'Type',
                                                        sortable: false,
                                                        Cell: ({cell}) => employeeContactTypes.find(t => t.code === cell.getValue())?.description
                                                    },
                                                    {
                                                        id: 'email',
                                                        accessorKey: 'email',
                                                        header: 'Email',
                                                        sortable: false,
                                                        minWidth: 200
                                                    },
                                                    {
                                                        id: 'phone',
                                                        accessorKey: 'phone',
                                                        header: 'Phone',
                                                        sortable: false
                                                    },
                                                ]}
                                                tableProps={{
                                                    enableSorting: false,
                                                    enableColumnResizing: false,
                                                    enableBottomToolbar: false,
                                                    enableTopToolbar: false,
                                                    enableRowActions: isEditMode,
                                                    muiTableContainerProps: {
                                                        sx: {
                                                            maxHeight: 500,
                                                            minHeight: 100
                                                        }
                                                    },
                                                    muiTableBodyRowProps: {hover: false},
                                                    renderRowActions: isEditMode
                                                        ? ({row}) => (
                                                            <Box display="flex" flexWrap="nowrap" gap={1}>
                                                                {getContactActions(row.original).map((action, idx) => (
                                                                    <Tooltip
                                                                        key={idx}
                                                                        title={!row.original.simsId && row.original.id
                                                                            ? 'The contact cannot be edited before it will be synchronized with SIMS'
                                                                            : null}
                                                                    >
                                                                        <span>
                                                                            <IconButton
                                                                                key={idx}
                                                                                onClick={action.onClick}
                                                                                disabled={!row.original.simsId && row.original.id}
                                                                            >
                                                                                {action.icon}
                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                ))}
                                                            </Box>
                                                        )
                                                        : undefined
                                                }}
                                            />
                                        )}

                                        <AdmicityDialog
                                            handleClose={() => setOpenContactDialog(false)}
                                            title={editingContact ? 'Edit Contact' : 'Add Contact'}
                                            open={openContactDialog}
                                            actions={[]}
                                        >
                                            <AdmicityForm
                                                id="contact-form"
                                                textFields={contactFormFields.map(field => ({
                                                    ...field,
                                                    initialValue: editingContact ? editingContact[field.name] : ''
                                                }))}
                                                handleSubmit={handleSaveContact}
                                                buttonText="Save"
                                                actionsContainerProps={{container: {display: 'flex'}}}
                                            />
                                        </AdmicityDialog>
                                    </ProfileInfoSection>

                                    <ProfileInfoSection title={
                                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                            <span>Next Of Kin</span>
                                            {isEditMode && (
                                                <Button
                                                    color="error"
                                                    variant="outlined"
                                                    startIcon={<ClearIcon/>}
                                                    onClick={handleClearNextOfKin}
                                                    size="medium"
                                                >
                                                    Delete
                                                </Button>
                                            )}
                                        </Box>
                                    }>
                                        <EditableFields fields={nextOfKinFields} edit={isEditMode}/>
                                    </ProfileInfoSection>

                                    <ProfileInfoSection title="Next Of Kin Address">
                                        <AdmicityAddressLookup
                                            key={
                                                isEditMode
                                                    ? `nok-edit-${nextOfKinAddressKey}`
                                                    : `nok-view-${nextOfKinAddressDataKey}`
                                            }
                                            onAddressSelect={isEditMode ? setSelectedNextOfKinAddress : ignoreAddressSelect}
                                            readonly={!isEditMode || !nextOfKinFields.nextOfKinSurname?.textFieldProps.value}
                                            defaultCountry={''}
                                            initialValue={isEditMode ? selectedNextOfKinAddress : data?.nextOfKinAddress}
                                        />
                                    </ProfileInfoSection>
                                </Grid>
                            </Grid>
                        )}

                        <Box display={'flex'} justifyContent={'end'}>
                            {isEditMode
                                ? (
                                    <Button
                                        sx={{width: 150}}
                                        variant="contained"
                                        onClick={handleSave}
                                        disabled={!isFormValid}
                                    >
                                        Save
                                    </Button>
                                )
                                : (
                                    Object.keys(data).length === 0 || data.id == null
                                        ? null
                                        : (
                                            <Button
                                                variant="contained"
                                                sx={{width: 150}}
                                                onClick={() => {
                                                    setSelectedAddress(data?.address || null);
                                                    setSelectedNextOfKinAddress(data?.nextOfKinAddress || null);
                                                    setCars(data?.cars || []);
                                                    setContacts(data?.contacts || []);
                                                    setIsEditMode(true);
                                                }}
                                            >
                                                Edit
                                            </Button>
                                        )
                                )
                            }
                        </Box>
                    </>
            }

            <Backdrop
                sx={{
                    position: 'absolute',
                    backgroundColor: 'rgb(250, 250, 251)',
                    zIndex: (theme) => theme.zIndex.drawer + 1
                }}
                open={isSomeQueryPending}
            >
                <Stack flexDirection="column" alignItems="center">
                    <CircularProgress size={40}/>
                </Stack>
            </Backdrop>
        </Box>
    );
};

EmployeeProfile.propTypes = {
    data: PropTypes.object,
    isLoading: PropTypes.bool,
};

export default EmployeeProfile;
